import React from "react";
import type { EnrollmentRow } from "./useEnrollmentSelection";

/**
 * The enrollment table shown inside every bulk-action modal, with a leading
 * checkbox column (header select-all + per-row) so the user can narrow the
 * pre-selected students before applying the action. Extracted from the
 * copy-pasted table that previously lived in each Bulk*Modal.
 */

export const headerCellStyle: React.CSSProperties = {
  background: "var(--surface-page)",
  color: "var(--text-body)",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-default)",
  textAlign: "left",
  whiteSpace: "nowrap",
};

export const bodyCellStyle: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: "0.8rem",
  color: "var(--text-strong)",
  borderBottom: "1px solid var(--border-subtle)",
};

interface Props {
  enrollments: EnrollmentRow[];
  selectedIds: Set<number>;
  allSelected: boolean;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  /** Header label above the table; defaults to a standard "Enrollments (n of m selected)". */
  caption?: React.ReactNode;
  /** Show the "Status" column (default true). */
  showStatus?: boolean;
  /** Optional extra trailing column (e.g. Promote's "New Student Code" input). */
  extraColumnHeader?: string;
  renderExtraCell?: (e: EnrollmentRow) => React.ReactNode;
  /** Disable all checkboxes (e.g. while submitting). */
  disabled?: boolean;
  /** Message shown when there are no enrollments. */
  emptyText?: string;
}

const SelectableEnrollmentTable: React.FC<Props> = ({
  enrollments,
  selectedIds,
  allSelected,
  onToggle,
  onToggleAll,
  caption,
  showStatus = true,
  extraColumnHeader,
  renderExtraCell,
  disabled = false,
  emptyText,
}) => {
  return (
    <>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--text-strong)",
          marginBottom: 6,
        }}
      >
        {caption ??
          `Enrollments (${selectedIds.size} of ${enrollments.length} selected)`}
      </div>

      {enrollments.length === 0 ? (
        <div
          style={{
            padding: 16,
            background: "#fefce8",
            color: "#854d0e",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {emptyText ??
            "The selected students have no enrollments. Close this dialog and pick rows that have at least one enrollment."}
        </div>
      ) : (
        <div
          style={{
            maxHeight: 260,
            overflow: "auto",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...headerCellStyle, width: 36, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={onToggleAll}
                    disabled={disabled}
                    style={{ margin: 0, cursor: disabled ? "default" : "pointer" }}
                  />
                </th>
                <th style={headerCellStyle}>Enrollment</th>
                <th style={headerCellStyle}>Student</th>
                {showStatus && <th style={headerCellStyle}>Status</th>}
                <th style={headerCellStyle}>Batch</th>
                {extraColumnHeader && (
                  <th style={headerCellStyle}>{extraColumnHeader}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => {
                const checked = selectedIds.has(e.enrollmentId);
                return (
                  <tr key={e.enrollmentId}>
                    <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggle(e.enrollmentId)}
                        disabled={disabled}
                        style={{ margin: 0, cursor: disabled ? "default" : "pointer" }}
                      />
                    </td>
                    <td style={bodyCellStyle}>
                      <span style={{ fontWeight: 600 }}>#{e.enrollmentId}</span>
                      {e.studentCode && (
                        <span style={{ marginLeft: 6, color: "var(--text-muted)" }}>
                          {e.studentCode}
                        </span>
                      )}
                    </td>
                    <td style={bodyCellStyle}>
                      <span style={{ color: "var(--text-strong)" }}>
                        {e.studentName}
                      </span>
                      <span
                        style={{
                          marginLeft: 6,
                          color: "var(--text-faint)",
                          fontSize: 11,
                        }}
                      >
                        Enq {e.enquiryId}
                      </span>
                    </td>
                    {showStatus && (
                      <td style={bodyCellStyle}>
                        <span style={{ color: "var(--text-body)" }}>
                          {e.statusName || "—"}
                        </span>
                      </td>
                    )}
                    <td style={bodyCellStyle}>
                      <span style={{ color: "var(--text-body)" }}>
                        {e.batchName || "—"}
                      </span>
                    </td>
                    {renderExtraCell && (
                      <td style={bodyCellStyle}>{renderExtraCell(e)}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default SelectableEnrollmentTable;
